import React from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { companyNameQuery } from '../../queries';
import { toggleAddNewItemModal } from '../../actions';
import LinkWithIcon from '../LinkWithIcon';
import Tooltip from '../Tooltip';
import LoginButton from '../LoginButton';
import HamburgerMenu from '../HamburgerMenu';
import Icon from '../Icon';
import SmallWhitePlus from '../../public/static/images/icon-small-white-plus.svg';
import Plus from '../../public/static/images/green-plus.svg';
import CloseWebview from '../../public/static/images/close-webview.svg';
import useScreenSize from '../../hooks/useScreenSize';
import useBugsnagNotifier from '../../hooks/useBugsnagNotifier';
import { useNewItemLocalState } from '../../hooks/useNewItemLocalState';
import { useBridgeAPI, useBridgeState } from '../../bridge';
import { getIsCurrentPage } from '../../utils/helpers/getIsCurrentPage';
import {
  ButtonsGroupContainer,
  FlexedHeader,
  MenuItem,
  MenuItemsContainer,
  BookmarkIconLink,
  WebViewButton,
  Wrapper,
  CreateItemIconLink
} from './styled';

const FlexibleHeader = ({ isShowCloseButton, isFixedHeader }) => {
  const { t } = useTranslation();
  const router = useRouter();
  const dispatch = useDispatch();
  const { isWebView } = useBridgeState();
  const { closeWebview } = useBridgeAPI();
  const { isSmall, isMedium, isLarge } = useScreenSize();
  const { setFirstViewValue } = useNewItemLocalState();
  const {
    data,
    error,
    loading: isLoadingProfile
  } = useQuery(companyNameQuery, {
    ssr: false
  });
  const isAuth = Boolean(data && data?.profile);
  useBugsnagNotifier(error);

  const onMobileIconPlusClick = () => {
    dispatch(toggleAddNewItemModal(true));
    setFirstViewValue('false');
  };

  const menuItemsList = [
    {
      title: t('main_menu.sale'),
      path: '/alqi-satqi',
      isShow: !isSmall,
      cypress: 'sale'
    },
    {
      title: t('main_menu.lease'),
      path: '/kiraye',
      isShow: !isSmall,
      cypress: 'lease'
    },
    {
      title: t('search.form.paid_daily'),
      path: '/kiraye?paid_daily=true',
      isShow: !isSmall,
      cypress: 'paid-daily'
    },
    {
      title: t('main_menu.agencies'),
      path: '/agentlikler',
      isShow: isLarge,
      cypress: 'agencies'
    },
    {
      title: t('main_menu.residences'),
      path: '/yasayis-kompleksleri',
      isShow: isLarge,
      cypress: 'residences'
    }
  ];

  const buttonsGroupList = [
    {
      button: (
        <Tooltip
          text={t('pages.bookmarks.title')}
          wrapperHeight={30}
          wrapperWidth={30}
          tooltipTopSpace={45}
        >
          <BookmarkIconLink
            data-cy="favorites-icon-btn"
            href="/bookmarks"
            data-stat="header-btn"
          />
        </Tooltip>
      ),
      isShow: !isSmall
    },
    {
      button: (
        <CreateItemIconLink
          href="/items/new"
          data-cy="header-add-new-item-btn"
          data-stat="header-add-new-item-btn"
        >
          <SmallWhitePlus />
          {t('common.create_new_item')}
        </CreateItemIconLink>
      ),
      isShow: isLarge
    },
    {
      button: (
        <LinkWithIcon
          cypress="add-new-icon-btn"
          href="/items/new"
          stat="header-btn"
          icon={Plus}
          width={30}
          height={30}
          indent={false}
        />
      ),
      isShow: isMedium
    },
    {
      button: (
        <i
          data-cy="add-new-icon-btn"
          data-stat="header-btn"
          onClick={onMobileIconPlusClick}
          onKeyPress={onMobileIconPlusClick}
          role="button"
          tabIndex="-1"
        >
          <Icon indent={false} IconComponent={Plus} width={30} height={30} />
        </i>
      ),
      isShow: isSmall && !isWebView
    },
    {
      button: <LoginButton {...{ isAuth, isLoadingProfile }} />,
      isShow: !isSmall
    },
    {
      button: (
        <WebViewButton onClick={closeWebview}>
          <i>
            <Icon
              IconComponent={CloseWebview}
              width={15}
              height={15}
              indent={false}
              color={'#a9a39e'}
            />
          </i>
        </WebViewButton>
      ),
      isShow: isSmall && isWebView && isShowCloseButton
    }
  ];

  return (
    <FlexedHeader data-cy="header" id="header" {...{ isFixedHeader }}>
      <Wrapper>
        <HamburgerMenu />
        <MenuItemsContainer data-cy="header-items">
          {menuItemsList.map(({ title, path, isShow, cypress }) => {
            if (!isShow) return null;

            const isCurrentPage = getIsCurrentPage(path, router);

            return (
              <MenuItem
                href={path}
                key={title}
                data-cy={cypress}
                {...{ isCurrentPage }}
              >
                {title}
              </MenuItem>
            );
          })}
        </MenuItemsContainer>
        <ButtonsGroupContainer>
          {buttonsGroupList.map(({ button, isShow }, index) => {
            if (!isShow) return null;

            return <React.Fragment key={index}>{button}</React.Fragment>;
          })}
        </ButtonsGroupContainer>
      </Wrapper>
    </FlexedHeader>
  );
};

FlexibleHeader.propTypes = {
  isShowCloseButton: PropTypes.bool,
  isFixedHeader: PropTypes.bool
};

export default FlexibleHeader;
